import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { useToast } from "@/hooks/use-toast";

export function useDocumentViewer(stepTitle: string, caseId: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const { data: document, refetch } = useQuery({
    queryKey: ['workflow-document', caseId, stepTitle],
    queryFn: async () => {
      if (!stepTitle || !caseId || !user?.id) {
        console.log('Missing required parameters for workflow document fetch:', {
          caseId,
          stepTitle,
          userId: user?.id
        });
        return null;
      }

      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          console.error('Session error:', sessionError);
          throw new Error('Authentication error');
        }

        console.log('Fetching workflow document:', {
          caseId,
          stepTitle,
          userId: user.id
        });

        const { data, error } = await supabase
          .from('documents')
          .select()
          .eq('case_id', caseId)
          .eq('title', stepTitle)
          .eq('user_id', user.id)
          .eq('document_type', 'workflow_generated')
          .maybeSingle();

        if (error) {
          console.error('Error fetching document:', error);
          throw error;
        }

        console.log('Document fetch result:', data);
        return data;

      } catch (error: any) {
        console.error('Error in workflow document fetch:', error);
        
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          toast({
            variant: "destructive",
            title: "Nettverksfeil",
            description: "Kunne ikke koble til serveren. Sjekk internettforbindelsen din."
          });
        } else {
          toast({
            variant: "destructive",
            title: "Feil ved henting av dokument",
            description: error.message || "Kunne ikke hente dokumentet. Vennligst prøv igjen."
          });
        }
        
        return null;
      }
    },
    enabled: Boolean(stepTitle && caseId && user?.id),
    retry: (failureCount, error) => {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return failureCount < 3;
      }
      return false;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
    refetchOnWindowFocus: false,
    staleTime: 30000
  });

  const handleDownload = async () => {
    if (!document) return;
    
    const blob = new Blob([document.content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = `${document.title}.txt`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return {
    isViewerOpen,
    setIsViewerOpen,
    document,
    handleDownload,
    refetchDocument: refetch
  };
}